import {QuoteWizardStateService} from '../quoteWizardState.service';
import {BusinessTypeGateway} from './gateways/businessType.gateway';
import {TextQuoteWizardStepTemplateProvider} from './templateProviders/textQuoteWizardStepTemplateProvider';
import IState = angular.ui.IState;
import {IQuoteWizardInputComponentStepTemplateProvider} from './templateProviders/iquoteWizardInputComponentStepTemplateProvider';
import {SelectQuoteWizardStepTemplateProvider} from './templateProviders/selectQuoteWizardStepTemplateProvider';
import {IQuoteWizardNode} from '../quoteWizardNode';
import {ReadonlyTextQuoteWizardStepTemplateProvider} from './templateProviders/readonlyTextQuoteWizardStepTemplateProvider';
import {AddressQuoteWizardInputComponentStepTemplateProvider} from './templateProviders/addressQuoteWizardStepTemplateProvider';
import {DateQuoteWizardStepTemplateProvider} from './templateProviders/dateQuoteWizardStepTemplateProvider';
import {NumberQuoteWizardStepTemplateProvider} from './templateProviders/numberQuoteWizardStepTemplateProvider';
import {YesNoQuoteWizardStepTemplateProvider} from './templateProviders/yesNoQuoteWizardStepTemplateProvider';
import {LocationAggregate} from '../../models/locationaggregate.model';
import {PercentOrDollarQuoteWizardStepTemplateProvider} from './templateProviders/percentOrDollarQuoteWizardStepTemplateProvider';
import {BrandGateway} from './gateways/brand.gateway';
import {PolicyGateway} from './gateways/policy.gateway';
import {ICode} from '../../models/code.model';
import {Constants} from '../../models/constants/marketplace.constants';
import { IBrand } from '../../models/models';

export interface IHelpConfig {
    title: string;
    text: string;
}

export interface IQuoteWizardStepsService {
    getWizardSteps(): IQuoteWizardNode[];
}

export interface IQuoteWizardStepsProvider extends angular.IServiceProvider {
    getWizardSteps(): IQuoteWizardNode[];
}

export class QuoteWizardStepsProvider implements IQuoteWizardStepsProvider {
    private wizardSteps: IQuoteWizardNode[];
    private rootStateName: string = 'shellFrame.quoteWizardFrame.wizard.';
    private static loginStateName = 'shellFrame.authenticationFrame.login';

    constructor() {
        this.$get.$inject = [];
    }

    public $get(): IQuoteWizardStepsService {
        return {
            getWizardSteps: () => {
                return this.getWizardSteps();
            }
        };
    }

    public getWizardSteps(): IQuoteWizardNode[] {
        if (angular.isUndefined(this.wizardSteps)) {
            this.populateWizardSteps();
        }
        return this.wizardSteps;
    }

    private contactInfoNodes(): IQuoteWizardNode[] {
        return [
            {
                stateName: this.rootStateName + 'fullName',
                headingText: 'What is your full name?',
                menuLabel: 'Contact name',
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/fullName/fullName.view.html',
                            controller: 'quoteWizardDefaultCustomView.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        },
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Contact name',
                            text: `The contact information entered in this section will be used as the 
                            primary contact for this insurance quote and the resulting insurance policies.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: true}},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'email',
                headingText: 'What is your email address?',
                menuLabel: 'Email',
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/email/email.view.html',
                            controller: 'email.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        },
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Email',
                            text: `Intrepid will use this email address to create your user account and send you 
                            information about your insurance quote once you have completed the application process.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: true}},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'phoneNumber',
                headingText: 'What is your phone number?',
                menuLabel: 'Phone number',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new TextQuoteWizardStepTemplateProvider(
                            'Phone number',
                            'vm.stateService.viewModel.phone.phoneNumber',
                            'required:alt=Phone number is a required field.',
                            undefined,
                            '(999) 999-9999',
                            '_'),
                            'phone.controller'),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Phone number',
                            text: `Intrepid will use this phone number to contact you if necessary to address any outstanding questions during the quoting process.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'mailingAddress',
                headingText: 'What mailing address would you like to use to receive official documents?',
                menuLabel: 'Mailing address',
                stateConfig: {
                    views: {
                        '': {
                            template: (new AddressQuoteWizardInputComponentStepTemplateProvider(
                                'vm.stateService.viewModel.clientStateContext.submissionRequest.mailingAddress.streetAddressLine1',
                                'vm.stateService.viewModel.clientStateContext.submissionRequest.mailingAddress.streetAddressLine2',
                                'vm.stateService.viewModel.clientStateContext.submissionRequest.mailingAddress.city',
                                'vm.stateService.viewModel.clientStateContext.submissionRequest.mailingAddress.state',
                                'vm.stateService.viewModel.clientStateContext.submissionRequest.mailingAddress.zipCode'
                            )).getTemplate(),
                            controller: 'mailingAddress.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        },
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Mailing address',
                            text: `This mailing address will be used to send correspondence.
                            The address that appears on the insurance policy may vary based on insurance regulatory requirements.
                            If you have any questions regarding the address that appears on your policy Intrepid Direct will be happy to review.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            }];
    }

    private businessInfoNodes(): IQuoteWizardNode[] {
        return [
            {
                stateName: this.rootStateName + 'brand',
                headingText: 'What brand is your franchise?',
                menuLabel: 'Brand',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new SelectQuoteWizardStepTemplateProvider(
                            'Brand',
                            'vm.stateService.viewModel.clientStateContext.submissionRequest.brandId',
                            'required:alt=Please select a brand.')),
                        'selectOptions': {
                            data: {
                                'optionsProvider': ['brand.gateway', 'quoteWizardStateService', (brandGateway: BrandGateway, stateService: QuoteWizardStateService) => {
                                    stateService.showViewLockIfStillWaitingOnPromise(stateService.viewModel.brandsPromise);
                                    return stateService.viewModel.brandsPromise.then((brands: IBrand[]) => {
                                        return brandGateway.getSelectInputOptionsProvider(brands, stateService.viewModel);
                                    });
                                }]
                            }
                        },
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Brand',
                            text: `Intrepid Direct is focused on the specific needs of the franchise restaurant owners.
                        We took our knowledge of the restaurant industry, listened to franchisees and reviewed franchisor insurance requirements to 
                        offer coverages that not only meet requirements, but best protect a franchise restaurant.
                        <br><br>
                        By providing Intrepid with your brand we can provide you with the quickest possible quoting process and provide you with an 
                        insurance product that best meets your specific needs.
                        <br><br>
                        <strong>What if my business operates more than one brand?</strong>
                        <br>
                        Select "Other" and Intrepid will work with you to create a package that works best for you.
                        <br><br>
                        <strong>What if my business operates a brand that is not listed?</strong>
                        <br>
                        Select "Other" and Intrepid will contact you to collect any additional information we need in order to provide you with a 
                        quote for your insurance product needs.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'legalBusinessName',
                headingText: 'What is your legal business name?',
                menuLabel: 'Business name',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new TextQuoteWizardStepTemplateProvider(
                            'Business name',
                            'vm.stateService.viewModel.clientStateContext.submissionRequest.legalBusinessName',
                            'required:alt=Business name is a required field.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Business name',
                            text: `For limited partnerships, LLCs, and corporations, the legal name of the business is the same name 
                                    registered with the state filing office (e.g. the name used in tax filings).
                                    <br><br>
                                    If you have multiple business names, enter one and we will ask for your additional names at a later time.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'effectiveDate',
                headingText: 'What is your effective date of coverage?',
                menuLabel: 'Effective date',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new DateQuoteWizardStepTemplateProvider(
                            'Effective date',
                            'vm.stateService.viewModel.clientStateContext.submissionRequest.policyEffectiveDate',
                            'required:alt=Effective date is a required field.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Effective date',
                            text: `The effective date is when you would like coverage to begin with Intrepid Direct.
                                <br><br>
                                In most cases, this date matches the expiration date of your current insurance policy.
                                <br><br>
                                If you are a new business, the effective date is typically when you open or have control of your store.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'businessType',
                headingText: 'What type of legal entity is your business?',
                menuLabel: 'Business type',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new SelectQuoteWizardStepTemplateProvider(
                            'Business type',
                            'vm.stateService.viewModel.clientStateContext.submissionRequest.businessTypeCId',
                            'required:alt=Please select a business type.')),
                        'selectOptions': {
                            data: {
                                'optionsProvider': ['businessType.gateway', 'quoteWizardStateService',
                                    (businessTypeGateway: BusinessTypeGateway, stateService: QuoteWizardStateService) => {
                                        stateService.showViewLockIfStillWaitingOnPromise(stateService.viewModel.businessTypesPromise);
                                        return stateService.viewModel.businessTypesPromise.then((businessTypes: ICode[]) => {
                                            return businessTypeGateway.getSelectInputOptionsProvider(businessTypes);
                                        });
                                    }]
                            }
                        }
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'yearEstablished',
                headingText: 'What year was your business established?',
                menuLabel: 'Year established',
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/yearEstablished/yearEstablished.view.html',
                            controller: 'quoteWizardDefaultCustomView.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        },
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Year established',
                            text: `Insurance pricing may be impacted by the number of years you have been in operation.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'currentlyInsured',
                headingText: 'Are you currently insured?',
                menuLabel: 'Currently insured',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.clientStateContext.submissionRequest.currentlyInsured',
                            'required:alt=Please select an option above.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Currently insured',
                            text: `We want to confirm that you currently have valid insurance in place with no lapse in coverage. If you are a new business, answer "No".`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'ownedOrLeasedVehicles',
                headingText: 'Do you have any owned or leased vehicles associated with the business?',
                menuLabel: 'Owned or leased vehicles',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.clientStateContext.submissionRequest.ownedVehicles',
                            'required:alt=Please select an option above.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Owned or leased vehicles',
                            text: `If your business owns or leases vehicles, Intrepid will contact you after the quoting process in order 
                        to collect the necessary information to provide you a quote for your commercial auto insurance needs.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'ownOtherBusinesses',
                headingText: 'Do you own any other businesses?',
                menuLabel: 'Ownership in other businesses',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.questionResponseGraph.ownedOtherBusinesses.response',
                            'required:alt=Please select an option above.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Ownership in other businesses',
                            text: `If you have ownership in any other businesses, we will want to confirm you have secured insurance for those entities elsewhere.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'cancelledCoverage',
                headingText: 'Has your insurance coverage been cancelled or non-renewed in the last 3 years?',
                menuLabel: 'Cancelled coverage',
                isIncluded: ['quoteWizardStateService', (quoteWizardStateService: QuoteWizardStateService) => {
                    return angular.isDefined(quoteWizardStateService.viewModel.clientStateContext.submissionRequest.mailingAddress)
                        && quoteWizardStateService.viewModel.clientStateContext.submissionRequest.mailingAddress.state !== 'MO';
                }],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.questionResponseGraph.cancelledCoverage.response',
                            'required:alt=Please select an option above.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Cancelled coverage',
                            text: `If you answer "Yes" to this question, we will have a conversation to learn more.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'currentlyHaveEpliCoverage',
                headingText: 'Do you currently have Employment Practices Liability coverage?',
                menuLabel: 'EPLI coverage',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.questionResponseGraph.currentlyHaveEpliCoverage.response',
                            'required:alt=Please select an option above.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'EPLI coverage',
                            text: `Employment Practices Liability is commonly purchased by restaurants to protect against claims such as discrimination or harassment.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'epliClaims',
                headingText: `Have you had any employment-related claims, either paid by insurance or directly by you, in the past 5 years?`,
                menuLabel: 'EPLI claims in the past 5 years',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.questionResponseGraph.epliClaims.response',
                            'required:alt=Please select an option above.',
                            'Examples: EEOC, Discrimination, Harassment, Wage and Hour')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'EPLI claims',
                            text: `We want to know whether or not you have had any incidents that could result in discrimination,
                            harassment, or an Equal Employment Opportunity Commission (EEOC) claim.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            }];
    }

    private locationNodes(): IQuoteWizardNode[] {
        return [
            {
                stateName: this.rootStateName + 'locationName',
                headingText: 'Would you like to give this store a name or number?',
                menuLabel: 'Store name',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new TextQuoteWizardStepTemplateProvider(
                            'Store name',
                            'vm.subject.name',
                            'required:alt=Store name is a required field.|max_len:100:alt=Store name cannot exceed 100 characters.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Store name',
                            text: `Many customers choose to use a store number or other common name assigned to their stores to quickly identify a location.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'locationAddress',
                headingText: 'What is the address of this location?',
                menuLabel: 'Address of location',
                stateConfig: {
                    views: {
                        '': {
                            template: (new AddressQuoteWizardInputComponentStepTemplateProvider(
                                'vm.subject.address.streetAddressLine1',
                                'vm.subject.address.streetAddressLine2',
                                'vm.subject.address.city',
                                'vm.subject.address.state',
                                'vm.subject.address.zipCode'
                            )).getTemplate(),
                            controller: 'quoteWizardDefaultViewController',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        },
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Address of location',
                            text: `This address should be the physical location of the restaurant.  
                            <br><br>
                            Intrepid utilizes an address verification service that provides additional information required to provide an insurance quote.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'ownBuilding',
                headingText: 'Do you own the building?',
                menuLabel: 'Own building',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.subject.ownBuilding',
                            'required:alt=Please select an option above.'),
                            'ownBuilding.controller'),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Own building',
                            text: `Many business owners lease their buildings with no responsibility for the actual building structure.
                            <br><br>
                            In some situations, your lease requirements may require you to insure the building. If so, answer Yes.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'buildingReplacementCost',
                headingText: 'What is the replacement cost of the building?',
                menuLabel: 'Replacement cost of building',
                isIncluded: ['subject', (subject: LocationAggregate) => subject.ownBuilding],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new NumberQuoteWizardStepTemplateProvider(
                            'Building value',
                            'vm.subject.buildingAmount',
                            'required:alt=Building value is a required field.|between_num:0,999999999:alt=Must be a number between 0 and 999,999,999',
                            true)),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Own building',
                            text: `BUILDING
                            <br><br>
                            The structure; including the walls, roof, and floors.
                            <br><br>
                            <u>Hint</u>: This amount is only needed if you own your building or are 
                            responsible by contract to insure the structure.
                            <br><br>
                            <u>Replacement Cost</u>: Both Building and Business Personal Property 
                            should be valued at replacement cost. It means the cost to replace 
                            the property on the same premises with other property of comparable 
                            material and quality used for the same purpose.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'businessPersonalPropertyValue',
                headingText: `What is the estimated replacement cost of all business personal property, equipment, including the build-out at this location?`,
                menuLabel: 'Business personal property value',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new NumberQuoteWizardStepTemplateProvider(
                            'Business personal property value',
                            'vm.subject.personalPropertyAmount',
                            'required:alt=Business personal property value is a required field.|between_num:100,999999999:alt=Must be a number between 100 and 999,999,999',
                            true)),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Business personal property value',
                            text: `BUSINESS PERSONAL PROPERTY
                            <br><br>
                            Includes contents and if leased space would also include building improvements.
                            <br><br>
                            <u>Hint</u>: Most restaurants lease their property; building improvements plus tables, 
                            chairs, ovens, stock, prep stations, lights, signs, POS systems, etc.
                            <br><br>
                            <u>Replacement Cost</u>: Both Building and Business Personal Property should be 
                            valued at replacement cost. It means the cost to replace the property on the same 
                            premises with other property of comparable material and quality used for the same purpose.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'totalAnnualSales',
                headingText: `What are the estimated total annual sales for this location?`,
                menuLabel: 'Total annual sales',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new NumberQuoteWizardStepTemplateProvider(
                            'Total sales',
                            'vm.subject.salesTotal',
                            'required:alt=Total sales is a required field.|between_num:0,999999999:alt=Must be a number between 0 and 999,999,999',
                            true)),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Total annual sales',
                            text: `Includes gross sales for this location including all delivery, catering, carry-out, etc.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'deliverySales',
                headingText: 'What are the annual delivery sales for this location?',
                menuLabel: 'Delivery sales',
                isIncluded: ['policy.gateway', 'quoteWizardStateService', (policyGateway: PolicyGateway, stateService: QuoteWizardStateService) => {
                    if (angular.isDefined(stateService.viewModel.applicablePolicies)) {
                        return policyGateway.isDeliveryAutoApplicable(stateService.viewModel.applicablePolicies);
                    }
                    return false;
                }],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new PercentOrDollarQuoteWizardStepTemplateProvider(
                            'vm.subject.salesDelivery',
                            'vm.subject.salesTotal',
                            'required:alt=Annual delivery sales is a required field.|' +
                            'between_num:0,{{vm.subject.salesTotal}}:alt=Dollar amount must be a number between 0 and the total sales amount of ${{vm.subject.salesTotal}}.'))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'cateringAndDeliverySales',
                headingText: 'What are the annual catering and delivery sales for this location?',
                menuLabel: 'Catering and Delivery sales',
                isIncluded: ['policy.gateway', 'quoteWizardStateService', (policyGateway: PolicyGateway, stateService: QuoteWizardStateService) => {
                    if (angular.isDefined(stateService.viewModel.applicablePolicies)) {
                        return !policyGateway.isDeliveryAutoApplicable(stateService.viewModel.applicablePolicies);
                    }
                    return true;
                }],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new PercentOrDollarQuoteWizardStepTemplateProvider(
                            'vm.subject.salesDelivery',
                            'vm.subject.salesTotal',
                            'required:alt=Annual catering and delivery sales is a required field.|' +
                            'between_num:0,{{vm.subject.salesTotal}}:alt=Dollar amount must be a number between 0 and the total sales amount of ${{vm.subject.salesTotal}}.'))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'haveLiquorSales',
                headingText: 'Do you have liquor sales at this location?',
                menuLabel: 'Liquor sales',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.subject.haveLiquorSales',
                            'required:alt=Please select an option above.'),
                            'haveLiquorSales.controller'),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Liquor sales',
                            text: `Any alcohol, beer, wine, or spirits would be considered liquor.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'liquorSalesAmount',
                headingText: 'What are the annual liquor sales for this location?',
                menuLabel: 'Liquor sales amount',
                isIncluded: ['subject', (subject: LocationAggregate) => subject.haveLiquorSales],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new PercentOrDollarQuoteWizardStepTemplateProvider(
                            'vm.subject.salesLiquor',
                            'vm.subject.salesTotal',
                            'required:alt=Annual liquor sales is a required field.|' +
                            'between_num:1,{{vm.subject.salesTotal}}:alt=Dollar amount must be a number between 1 and the total sales amount of ${{vm.subject.salesTotal}}.')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Liquor sales amount',
                            text: `Portion of total annual sales that are attributable to the sale of all alcoholic beverages.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'restaurantPayroll',
                headingText: 'What is the estimated annual restaurant payroll for this location?',
                menuLabel: 'Restaurant payroll',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new NumberQuoteWizardStepTemplateProvider(
                            'Restaurant payroll excluding delivery payroll',
                            'vm.subject.payrollRestaurant',
                            'required:alt=Annual restaurant payroll is a required field.|between_num:0,999999999:alt=Must be a number between 0 and 999,999,999',
                            true))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'deliveryPayroll',
                headingText: 'What is the estimated annual delivery payroll for this location?',
                menuLabel: 'Delivery payroll',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new NumberQuoteWizardStepTemplateProvider(
                            'Delivery payroll',
                            'vm.subject.payrollDelivery',
                            'required:alt=Delivery payroll is a required field.|between_num:0,999999999:alt=Must be a number between 0 and 999,999,999',
                            true))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'fullTimeEmployees',
                headingText: 'How many full-time employees at this location?',
                menuLabel: 'Full-time employees',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new NumberQuoteWizardStepTemplateProvider(
                            'Full-time employees',
                            'vm.subject.numberOfFullTimeEmployees',
                            'required:alt=Full-time employees is a required field.|between_num:0,99999:alt=Must be a number between 0 and 99,999')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Full-time employees',
                            text: `Number of full-time employees primarily working at this location.
                        If an employee works at multiple locations they should only be included in the employee count for a single location.
                        <br><br>
                        Full-Time employee is defined as any employee that works 30 hours per week or more.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            {
                stateName: this.rootStateName + 'partTimeEmployees',
                headingText: 'How many part-time employees at this location?',
                menuLabel: 'Part-time employees',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new NumberQuoteWizardStepTemplateProvider(
                            'Part-time employees',
                            'vm.subject.numberOfPartTimeEmployees',
                            'required:alt=Part-time employees is a required field.|between_num:0,99999:alt=Must be a number between 0 and 99,999')),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Part-time employees',
                            text: `Number of part-time employees primarily working at this location.
                        If an employee works at multiple locations they should only be included in the employee count for a single location.
                        <br><br>
                        Part-time employee is defined as any employee that works less than 30 hours per week.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            }];
    }

    private deliverOperationNodes(): IQuoteWizardNode[] {
        return [
            {
                stateName: this.rootStateName + 'safetyProgram',
                headingText: 'Do you have a driver safety program or drivers requirements that include the following?',
                menuLabel: 'Safety program',
                isIncluded: ['policy.gateway', 'quoteWizardStateService', (policyGateway: PolicyGateway, stateService: QuoteWizardStateService) => {
                    return angular.isDefined(stateService.viewModel.applicablePolicies) && policyGateway.isDeliveryAutoApplicable(stateService.viewModel.applicablePolicies);
                }],
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/driverSafetyProgram/driverSafetyProgram.view.html',
                            controller: 'quoteWizardDefaultCustomView.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        }
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'deliveryVehiclesInspected',
                headingText: 'Are delivery vehicles inspected at hire?',
                menuLabel: 'Vehicle inspection at hire',
                isIncluded: ['policy.gateway', 'quoteWizardStateService', (policyGateway: PolicyGateway, stateService: QuoteWizardStateService) => {
                    return angular.isDefined(stateService.viewModel.applicablePolicies) && policyGateway.isDeliveryAutoApplicable(stateService.viewModel.applicablePolicies);
                }],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.questionResponseGraph.deliveryVehiclesInspected.response',
                            'required:alt=Please select an option above.'))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'bicycleDelivery',
                headingText: 'Do you have any delivery by bicycle, scooter or motorcycle?',
                menuLabel: 'Bicycle delivery',
                isIncluded: ['policy.gateway', 'quoteWizardStateService', (policyGateway: PolicyGateway, stateService: QuoteWizardStateService) => {
                    return angular.isDefined(stateService.viewModel.applicablePolicies) && policyGateway.isDeliveryAutoApplicable(stateService.viewModel.applicablePolicies);
                }],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.questionResponseGraph.bicycleDelivery.response',
                            'required:alt=Please select an option above.'))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            }];
    }

    private claimsHistoryNodes(): IQuoteWizardNode[] {
        return [
            {
                stateName: this.rootStateName + 'claimsInLastThreeYears',
                headingText: 'Have you had any insurance claims in the past 3 years?',
                menuLabel: 'Claims past 3 years',
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/claimsInLastThreeYears/claimsInLastThreeYears.view.html',
                            controller: 'claimsInLastThreeYears.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        }
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'claimTypes',
                headingText: 'Which types of claims have you had in the last 3 years?',
                menuLabel: 'Claim types',
                isIncluded: ['quoteWizardStateService', (stateService: QuoteWizardStateService) =>
                    stateService.viewModel.questionResponseGraph.anyClaimsInLastThreeYears.response === Constants.ClaimsInLastThreeYearsQuestionResponses.Yes],
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/claimTypes/claimTypes.view.html',
                            controller: 'claimTypes.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        },
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Claim types',
                            text: `<ul>
<li><strong>Property:</strong> Damage to your property due to fire, wind, hail, theft, employee dishonesty or  business interruption.</li>
<li><strong>Liability:</strong> Customer injured on your premises or from your products. (e.g. a slip and fall or food illness)</li>
<li><strong>Work Comp:</strong> Employee injury while working for you such as cuts, burns, slip and falls or auto accidents.</li>
<li><strong>Owned Auto:</strong> An owned or leased vehicle in an accident.</li>
<li><strong>Non-Owned Auto:</strong>
Bodily injury or property damage from an auto accident involving an employee using his own vehicle while driving for you</li> 
<li><strong>Employee Practice Liability:</strong>
Arising out of an employee complaint of discrimination, harassment, retaliation or wage and hour violations (overtime, minimum wage).</li>
<li><strong>Other:</strong>
If you are unsure of a category of a claim you have incurred, you can put other. Examples: Flood to your property, umbrella claim (large claim), cyber loss.</li>
</ul>`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'claimTypesOverLimit',
                headingText: 'Have you had any large claims?',
                menuLabel: 'Large claims',
                isIncluded: ['quoteWizardStateService', (stateService: QuoteWizardStateService) =>
                    stateService.viewModel.questionResponseGraph.anyClaimsInLastThreeYears.response === Constants.ClaimsInLastThreeYearsQuestionResponses.Yes],
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/claimsOverLimit/claimsOverLimit.view.html',
                            controller: 'quoteWizardDefaultCustomView.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        }
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'robberyOrAssaultClaims',
                headingText: 'Have you had any claims from robberies or assaults?',
                menuLabel: 'Robberies or assaults',
                isIncluded: ['quoteWizardStateService', (stateService: QuoteWizardStateService) => {
                    // show only if claims in last 3 years
                    // show only if there were work comp or property claims
                    // UNLESS they've previously answered that there were assaults or robberies, in which case always keep that answer around
                    return stateService.viewModel.questionResponseGraph.anyClaimsFromRobberiesOrAssaults.response === true
                    || (stateService.viewModel.questionResponseGraph.anyClaimsInLastThreeYears.response === Constants.ClaimsInLastThreeYearsQuestionResponses.Yes
                        && (stateService.viewModel.questionResponseGraph.anyPropertyClaimsInLastThreeYears.response === true ||
                            stateService.viewModel.questionResponseGraph.anyWorkCompClaimsInLastThreeYears.response === true));
                }],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new YesNoQuoteWizardStepTemplateProvider(
                            'vm.stateService.viewModel.questionResponseGraph.anyClaimsFromRobberiesOrAssaults.response',
                            'required:alt=Please select an option above.'))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            }];
    }

    private quoteSummaryNodes(): IQuoteWizardNode[] {
        return [
            {
                stateName: this.rootStateName + 'quoteSummary',
                headingText: 'Application Complete!',
                menuLabel: 'Your quote',
                isIncluded: ['quoteWizardStateService',
                    (stateService: QuoteWizardStateService) => {
                        return (angular.isDefined(stateService.viewModel.finishedSubmissionResponse) &&
                            stateService.viewModel.finishedSubmissionResponse.submissionStatus === Constants.SubmissionStatus.Quoted);
                    }],
                stateConfig: {
                    templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/quoteSummary/quoteSummary.view.html',
                    controller: 'quoteSummary.controller',
                    controllerAs: 'vm',
                    params: {'wizardParams': undefined},
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'quoteNextSteps',
                headingText: 'Intrepid will take it from here.',
                menuLabel: 'Next steps',
                isIncluded: ['quoteWizardStateService',
                    (stateService: QuoteWizardStateService) => {
                        return (angular.isDefined(stateService.viewModel.finishedSubmissionResponse) &&
                            stateService.viewModel.finishedSubmissionResponse.submissionStatus === Constants.SubmissionStatus.Quoted);
                    }],
                stateConfig: {
                    templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/quoteNextSteps/quoteNextSteps.view.html',
                    controller: 'quoteWizardDefaultCustomView.controller',
                    controllerAs: 'vm',
                    params: {'wizardParams': undefined},
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            },
            {
                stateName: this.rootStateName + 'referSummary',
                headingText: 'You are done!!!',
                menuLabel: 'Next steps',
                isIncluded: ['quoteWizardStateService',
                    (stateService: QuoteWizardStateService) => {
                        return (angular.isUndefined(stateService.viewModel.finishedSubmissionResponse) ||
                            stateService.viewModel.finishedSubmissionResponse.submissionStatus === Constants.SubmissionStatus.Referred);
                    }],
                stateConfig: {
                    templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/referSummary/referSummary.view.html',
                    controller: 'referSummary.controller',
                    controllerAs: 'vm',
                    params: {'wizardParams': undefined},
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                }
            }
        ];
    }

    private populateWizardSteps(): void {

        const contactInfoNodes = this.contactInfoNodes();
        const businessInfoNodes = this.businessInfoNodes();
        const locationNodes = this.locationNodes();
        const deliverOperationNodes = this.deliverOperationNodes();
        const claimsHistoryNodes = this.claimsHistoryNodes();
        const quoteSummaryNodes = this.quoteSummaryNodes();

        this.wizardSteps = [
            /*
            Contact Info Section
             */
            {
                stateName: this.rootStateName + 'contactInfoBumper',
                headingText: 'In this section',
                sectionText: 'Contact Info',
                menuLabel: 'Contact Info',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new ReadonlyTextQuoteWizardStepTemplateProvider(
                            `First, let's get some basic info about you.`)),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Contact info',
                            text: `The contact information entered in this section will be used as the 
                            primary contact for this insurance quote and the resulting insurance policies.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: true}},
                    resolve: {}
                },
                subjectProvider: [() => {
                    return ['ContactInfoSubject'];
                }],
                repeatedNodes: contactInfoNodes
            },
            /*
            Business Info Section
             */
            {
                stateName: this.rootStateName + 'businessInfoBumper',
                headingText: 'In this section',
                sectionText: 'Business Info',
                menuLabel: 'Business Info',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new ReadonlyTextQuoteWizardStepTemplateProvider(
                            `Next let's gather information on your business.`)),
                        'help': QuoteWizardStepsProvider.getHelpState({
                            title: 'Business Info',
                            text: `This section will collect information about your restaurant business for the requested insurance coverage.`
                        })
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                },
                subjectProvider: [() => {
                    return ['BusinessInfoSubject'];
                }],
                repeatedNodes: businessInfoNodes
            },
            /*
            Location Section
             */
            {
                stateName: this.rootStateName + 'locationBumper',
                headingText: 'In this section',
                sectionText: 'Location',
                menuLabel: 'Location',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new ReadonlyTextQuoteWizardStepTemplateProvider(
                            `Now let’s add a location and go through a few questions about it.`))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                },
                subjectProvider: ['quoteWizardStateService', (stateService: QuoteWizardStateService) => stateService.viewModel.clientStateContext.submissionRequest.locations],
                repeatedNodes: locationNodes
            },
            {
                stateName: this.rootStateName + 'addAnotherLocation',
                headingText: 'Add another location?',
                sectionText: 'Location Summary',
                menuLabel: 'Location Summary',
                stateConfig: {
                    views: {
                        '': {
                            templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/addAnotherLocation/addAnotherLocation.view.html',
                            controller: 'addAnotherLocation.controller',
                            controllerAs: 'vm',
                            params: {'wizardParams': undefined},
                            resolve: {}
                        }
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {},
                    params: {'wizardParams': undefined}
                }
            },
            /*
            Delivery Operation Section
             */
            {
                stateName: this.rootStateName + 'deliveryOperationBumper',
                headingText: 'In this section',
                sectionText: 'Delivery Operations',
                menuLabel: 'Delivery Operations',
                isIncluded: ['policy.gateway', 'quoteWizardStateService', (policyGateway: PolicyGateway, stateService: QuoteWizardStateService) => {
                    return angular.isDefined(stateService.viewModel.applicablePolicies) && policyGateway.isDeliveryAutoApplicable(stateService.viewModel.applicablePolicies);
                }],
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new ReadonlyTextQuoteWizardStepTemplateProvider(
                            `Next, a few questions about driver safety.`))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                },
                subjectProvider: [() => {
                    return ['DeliveryOperationSubject'];
                }],
                repeatedNodes: deliverOperationNodes
            },
            /*
            Claims History Section
            */
            {
                stateName: this.rootStateName + 'claimsHistoryBumper',
                headingText: 'In this section',
                sectionText: 'Claims History',
                menuLabel: 'Claims History',
                stateConfig: {
                    views: {
                        '': QuoteWizardStepsProvider.getSingleInputComponentStepState(new ReadonlyTextQuoteWizardStepTemplateProvider(
                            `Next, we'll ask some questions about the business's claims history`))
                    },
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                },
                subjectProvider: [() => {
                    return ['ClaimsHistorySubject'];
                }],
                repeatedNodes: claimsHistoryNodes
            },
            /*
            Quote Summary Section
             */
            {
                stateName: this.rootStateName + 'submitRequest',
                headingText: 'One last click!',
                sectionText: 'Results',
                menuLabel: 'Results',
                stateConfig: {
                    templateUrl: 'modules/quoteWizard/quoteWizardSteps/custom/submitRequest/submitRequest.view.html',
                    controller: 'submitRequest.controller',
                    controllerAs: 'vm',
                    params: {'wizardParams': undefined},
                    data: {claimsProfile: {allowAnonymousAccess: false}, redirectToSignOn: QuoteWizardStepsProvider.loginStateName},
                    resolve: {}
                },
                subjectProvider: [() => {
                    return ['QuoteSummarySubject'];
                }],
                repeatedNodes: quoteSummaryNodes
            }
        ];
    }

    private static getHelpState(helpConfig: IHelpConfig): IState {
        return {
            template: `<h1>${helpConfig.title}</h1><p>${helpConfig.text}</p>`
        };
    }

    private static getSingleInputComponentStepState(templateProvider: IQuoteWizardInputComponentStepTemplateProvider, controller?: string): IState {
        return {
            params: {'wizardParams': undefined},
            template: templateProvider.getTemplate(),
            controller: angular.isDefined(controller) ? controller : 'quoteWizardDefaultViewController',
            controllerAs: 'vm',
            resolve: {}
        };
    }
}
